import { createSelector } from '@reduxjs/toolkit'
import { ServiceLimitType } from '@/models/service'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import {
  mapSelectedElementHasVisibleSeatsSelector,
  mapSelectedElementServices,
} from '@/store/mapSlice'
import { getServiceMinimumQuantity } from '@/utils/getServiceMinimumQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { getVisibleServices } from '@/utils/getVisibleServices'
import { handleError } from '@/utils/handleError'
import { getBoughtServicesTotal } from '@/utils/timeSchedules'
import { cartSlice } from '..'
import { activeElementQuotedSeatsAmountSelector } from './activeElementQuotedSeatsAmountSelector'

type MaxQtyErr = {
  type: 'maxQty'
  maxQty: number
}

type MinQtyErr = {
  type: 'minQty'
  minQty: number
}

type Errors = MaxQtyErr | MinQtyErr

type TicketError = {
  groupId: number
  serviceId: number
  serviceName: string
} & Errors

export const mapPageErrorsSelector = createSelector(
  [
    cartSlice.selectors.selectedElementReservation,
    mapSelectedElementServices,
    mapSelectedElementHasVisibleSeatsSelector,
    dayDifferenceSelector,
    activeElementQuotedSeatsAmountSelector,
  ],
  (
    selectedElementReservation,
    mapSelectedElementServices,
    selectedElementHasVisibleSeats,
    dayDifference,
    selectedElementQuotedSeatsAmount
  ): TicketError[] => {
    try {
      const errors: TicketError[] = []
      if (!selectedElementReservation) {
        return errors
      }

      const boughServicesMap = new Map<number, number>()

      Object.entries(selectedElementReservation.boughtServices).forEach(
        (entry) => {
          const [key, boughtServices] = entry
          const serviceId = Number(key)

          boughServicesMap.set(
            serviceId,
            getBoughtServicesTotal(boughtServices)
          )
        }
      )

      const visibleTicketServices = getVisibleServices({
        selectedElementHasVisibleSeats,
        services: mapSelectedElementServices.ticketServices,
      })

      visibleTicketServices.forEach((service) => {
        const boughtAmout = boughServicesMap.get(service.serviceId)

        // minQty
        const minimumQuantity = getServiceMinimumQuantity(
          service,
          selectedElementQuotedSeatsAmount,
          dayDifference
        )

        if (
          minimumQuantity > 0 &&
          (typeof boughtAmout === 'undefined' || boughtAmout < minimumQuantity)
        ) {
          errors.push({
            groupId: service.serviceGroupId,
            minQty: minimumQuantity,
            serviceId: service.serviceId,
            serviceName: service.name,
            type: 'minQty',
          })
        }

        // maxQty
        // this can only happen for services with the `NUMBER_OF_PIECES` limit
        if (service.maximumLimitType === ServiceLimitType.NUMBER_OF_PIECES) {
          const ticketLimit = getServiceSingleLimit({
            dayDifference,
            seatsAmount: selectedElementQuotedSeatsAmount,
            service,
            serviceInCartQuantity: boughtAmout,
          })

          if (typeof boughtAmout === 'number' && boughtAmout > ticketLimit) {
            errors.push({
              groupId: service.serviceGroupId,
              maxQty: ticketLimit,
              serviceId: service.serviceId,
              serviceName: service.name,
              type: 'maxQty',
            })
          }
        }
      })

      return errors
    } catch (error) {
      handleError(error, {
        tags: {
          selector: 'mapPageErrorsSelector',
        },
      })
    }

    return []
  }
)
